/**
 * StatusBadge — Tinted background + dot + label
 *
 * Differs from Badge (which is solid fill). StatusBadge uses a
 * semi-transparent tinted background with a small colored dot.
 *
 * Used for: product status (Myynnissä, Luonnos, Myyty), store open/closed
 */
import { styled, XStack, YStack, Text as TamaguiText, type GetProps } from 'tamagui'

const StatusBadgeFrame = styled(XStack, {
  name: 'StatusBadge',
  ai: 'center',
  gap: 6,
  px: 10,
  py: 4,
  br: '$full',

  variants: {
    variant: {
      success: { bg: 'rgba(116, 140, 112, 0.12)' },
      warning: { bg: 'rgba(232, 180, 156, 0.12)' },
      error: { bg: 'rgba(239, 68, 68, 0.12)' },
      info: { bg: 'rgba(59, 130, 246, 0.12)' },
      muted: { bg: 'rgba(133, 139, 128, 0.13)' },
    },
  } as const,

  defaultVariants: {
    variant: 'muted',
  },
})

const StatusDot = styled(YStack, {
  name: 'StatusDot',
  w: 6,
  h: 6,
  br: '$full',

  variants: {
    variant: {
      success: { bg: '$colorSuccess' },
      warning: { bg: '$colorWarning' },
      error: { bg: '$colorError' },
      info: { bg: '$colorInfo' },
      muted: { bg: '$colorMuted' },
    },
  } as const,

  defaultVariants: {
    variant: 'muted',
  },
})

const StatusLabel = styled(TamaguiText, {
  name: 'StatusLabel',
  fontFamily: '$body',
  fontSize: 12,
  fontWeight: '600',

  variants: {
    variant: {
      success: { color: '$colorSuccess' },
      warning: { color: '$colorWarning' },
      error: { color: '$colorError' },
      info: { color: '$colorInfo' },
      muted: { color: '$colorMuted' },
    },
  } as const,

  defaultVariants: {
    variant: 'muted',
  },
})

type StatusBadgeFrameProps = GetProps<typeof StatusBadgeFrame>
type StatusVariant = 'success' | 'warning' | 'error' | 'info' | 'muted'

interface StatusBadgeProps extends Omit<StatusBadgeFrameProps, 'variant'> {
  variant?: StatusVariant
  label: string
}

export const StatusBadge = StatusBadgeFrame.styleable<StatusBadgeProps>(
  ({ variant = 'muted', label, ...props }, ref) => {
    return (
      <StatusBadgeFrame ref={ref} variant={variant} {...props}>
        <StatusDot variant={variant} />
        <StatusLabel variant={variant}>{label}</StatusLabel>
      </StatusBadgeFrame>
    )
  },
)
